"use client";

import { TextShimmer } from "@/components/text-shimmer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BLUR_DATA_URL } from "@/config/site";
import { encodedHolidayParams } from "@/feature/holiday/search/lib/holiday-formatter-helper";
import { absoluteUrl } from "@/lib/utils";
import {
  ArrowUpRight,
  CalendarDays,
  Compass,
  MapPin,
  Palmtree,
  Sparkles,
} from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useTransition } from "react";
import {
  IHolidaySearchQueryParams,
  IHolidaySearchResult,
} from "../../types/holiday-search-type";

interface HolidayGridCardProps {
  holiday: IHolidaySearchResult;
  searchData: IHolidaySearchQueryParams;
}

function getImageUrl(path: string | null | undefined): string {
  if (!path) return "https://placehold.co/600x400/png";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return absoluteUrl(path, true);
}

function formatAudienceLabel(holidayFor?: string | null): string {
  if (!holidayFor) return "All travelers";
  const normalized = holidayFor.trim().toUpperCase();
  if (normalized === "BOTH" || normalized === "ALL") return "All travelers";
  if (normalized === "FAMILY") return "Family";
  if (normalized === "COUPLE") return "Couples";
  if (normalized === "SOLO") return "Solo";
  if (normalized === "GROUP") return "Group";
  return `${holidayFor.charAt(0).toUpperCase()}${holidayFor.slice(1).toLowerCase()}`;
}

export default function HolidayGridCard({
  holiday,
  searchData,
}: HolidayGridCardProps) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const holidayId = holiday.slug;
  const listedCities = holiday.cities ?? [];
  const cityChips = listedCities.slice(0, 3);
  const extraCityCount = Math.max(listedCities.length - cityChips.length, 0);

  const destinationText =
    listedCities
      .map((city) =>
        city.country ? `${city.city_name}, ${city.country}` : city.city_name,
      )
      .filter(Boolean)
      .join(" • ") ||
    searchData.city ||
    searchData.country ||
    null;

  const params = encodedHolidayParams(searchData);
  const detailsHref = params
    ? `/holiday/${encodeURIComponent(holidayId)}?${params}`
    : `/holiday/${encodeURIComponent(holidayId)}`;

  const parsedPrice = holiday.price ? Number(holiday.price) : NaN;
  const hasPrice = Number.isFinite(parsedPrice) && parsedPrice > 0;

  const rawDiscount =
    holiday.discount_price ??
    (holiday.discount != null ? String(holiday.discount) : undefined);
  const parsedDiscount = rawDiscount ? Number(rawDiscount) : 0;
  const rawDiscountType = holiday.discount_type ?? "";
  const isPercentageDiscount =
    rawDiscountType.toUpperCase() === "PER" ||
    rawDiscountType.toUpperCase() === "PERCENT" ||
    rawDiscountType.toUpperCase() === "PERCENTAGE" ||
    rawDiscountType === "%";
  const hasDiscount = Number.isFinite(parsedDiscount) && parsedDiscount > 0;

  let originalPrice: number | null = null;
  if (hasPrice && hasDiscount) {
    if (isPercentageDiscount && parsedDiscount < 100) {
      originalPrice = Math.round(parsedPrice / (1 - parsedDiscount / 100));
    } else if (!isPercentageDiscount && parsedDiscount > 0) {
      originalPrice = parsedPrice + parsedDiscount;
    }
  }

  const audienceLabel = formatAudienceLabel(holiday.holiday_for);

  const handleViewDetails = () => {
    startTransition(() => {
      router.push(detailsHref);
    });
  };

  return (
    <div className="group relative flex h-full w-full flex-col overflow-hidden rounded-2xl border border-border/70 bg-card shadow-xs transition-all duration-500 hover:-translate-y-1 hover:border-primary/40 hover:shadow-xl">
      {/* 1. Image Showcase with Glass Badges */}
      <div
        className="relative h-48 sm:h-52 w-full cursor-pointer overflow-hidden bg-muted/40"
        onClick={handleViewDetails}
      >
        {holiday.image ? (
          <Image
            src={getImageUrl(holiday.image)}
            alt={holiday.title}
            fill
            className="object-cover transition-transform duration-700 ease-out group-hover:scale-108"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            quality={75}
            placeholder="blur"
            blurDataURL={BLUR_DATA_URL}
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center bg-gradient-to-br from-primary/10 via-muted to-muted/80 p-3 text-center">
            <Palmtree className="size-8 text-primary/40 mb-1" />
            <span className="text-xs text-muted-foreground font-medium line-clamp-1">
              {holiday.title}
            </span>
          </div>
        )}

        {/* Cinematic Vignette Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-black/35" />

        {/* Top Badges (Category & Discount) */}
        <div className="absolute left-3 top-3 right-3 flex items-center justify-between gap-1.5 z-10 pointer-events-none">
          {holiday.holiday_type ? (
            <div className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-black/45 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur-md shadow-xs">
              <Compass className="size-3 text-amber-300 shrink-0" />
              <span>{holiday.holiday_type}</span>
            </div>
          ) : (
            <span />
          )}

          {hasDiscount ? (
            <div className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-rose-500 to-red-600 px-2.5 py-1 text-[10px] font-bold text-white shadow-xs">
              <Sparkles className="size-2.5 shrink-0" />
              <span>
                {isPercentageDiscount
                  ? `${parsedDiscount}% OFF`
                  : `Save BDT ${parsedDiscount.toLocaleString()}`}
              </span>
            </div>
          ) : null}
        </div>

        {/* Bottom Destination Overlay */}
        {destinationText ? (
          <div className="absolute inset-x-3 bottom-3 z-10 pointer-events-none">
            <div className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-white/15 bg-black/55 px-2.5 py-1 text-xs font-medium text-white backdrop-blur-md">
              <MapPin className="size-3.5 shrink-0 text-rose-400" />
              <span className="truncate">{destinationText}</span>
            </div>
          </div>
        ) : null}
      </div>

      {/* 2. Content Body */}
      <div className="flex flex-1 flex-col justify-between p-4 sm:p-4.5 gap-3">
        <div className="space-y-2.5">
          {/* Title */}
          <h3
            onClick={handleViewDetails}
            className="cursor-pointer line-clamp-2 text-base font-bold leading-snug tracking-tight text-foreground transition-colors duration-300 group-hover:text-primary"
          >
            {holiday.title}
          </h3>

          {/* Blue accent left-border box */}
          <div className="border-l-2 border-primary pl-2.5 py-0.5 space-y-0.5">
            <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
              <CalendarDays className="size-3.5 text-primary shrink-0" />
              <span>
                {holiday.duration
                  ? `${holiday.duration} Days Tour Package`
                  : "Holiday Package"}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {audienceLabel}
              {listedCities.length > 0
                ? ` • ${listedCities.length} ${listedCities.length === 1 ? "destination" : "destinations"}`
                : ""}
            </p>
          </div>

          {/* Cities Chips */}
          {listedCities.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1 pt-0.5">
              {cityChips.map((city) => (
                <Badge
                  key={`${holiday.id}-${city.city_id}`}
                  variant="secondary"
                  className="rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/15"
                >
                  <MapPin className="size-3 mr-1 text-sky-600" />
                  {city.city_name}
                </Badge>
              ))}
              {extraCityCount > 0 ? (
                <Badge
                  variant="secondary"
                  className="rounded-full px-2 py-0.5 text-[10px]"
                >
                  +{extraCityCount}
                </Badge>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* 3. Footer Pricing & CTA */}
        <div className="mt-auto border-t border-border/50 bg-gradient-to-b from-transparent to-muted/20 px-4 py-3 -mx-4 -mb-4 sm:px-4.5 sm:-mx-4.5 sm:-mb-4.5 dark:bg-muted/10">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                Starting from
              </p>
              {hasPrice ? (
                <div>
                  {originalPrice && originalPrice > parsedPrice ? (
                    <p className="text-[10px] text-muted-foreground line-through">
                      BDT {originalPrice.toLocaleString()}
                    </p>
                  ) : null}
                  <p className="text-lg sm:text-xl font-extrabold leading-tight text-foreground truncate">
                    <span className="mr-1 text-xs font-bold text-primary">
                      BDT
                    </span>
                    {parsedPrice.toLocaleString()}
                  </p>
                </div>
              ) : (
                <p className="text-xs font-bold text-primary">
                  Price on request
                </p>
              )}
            </div>

            <Button
              onClick={handleViewDetails}
              size="sm"
              className="group/btn h-9 rounded-xl px-3.5 text-xs font-semibold shadow-xs bg-primary hover:bg-primary/90 text-primary-foreground transition-all duration-300 hover:shadow-md"
              disabled={isPending}
            >
              {isPending ? (
                <TextShimmer>...</TextShimmer>
              ) : (
                <div className="flex items-center gap-1">
                  <span>Explore</span>
                  <ArrowUpRight className="size-3.5 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                </div>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
